import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  HelpTip,
  ROOM_ADMIN_ROLES,
  ROOM_ADMIN_ROLE_HINTS,
  ROOM_ADMIN_ROLE_LABELS,
  ROOM_LIFECYCLE_TRANSITIONS,
} from '@dataroom/shared';
import type { GrantWarning, Room, RoomAdminRole, RoomLifecycleState } from '@dataroom/shared';
import type { PlanLimitInfo } from '@/lib/plan';
import { GrantWarningList } from './GrantWarningList';
import { Modal } from './Modal';
import { PlanLimitDialog } from './PlanLimitDialog';

// Rooms are Active / Archived / In Trash. `draft` (retired) and `closed` (a
// retired alias the backend maps to archived) can only appear on legacy rows, so
// they render as Active / Archived respectively.
const STATUS_LABELS: Record<RoomLifecycleState, string> = {
  draft: 'Active',
  active: 'Active',
  archived: 'Archived',
  closed: 'Archived',
  trashed: 'In Trash',
};

const STATUS_STYLES: Record<RoomLifecycleState, string> = {
  draft: 'bg-st-certified text-st-certified-fg',
  active: 'bg-st-certified text-st-certified-fg',
  archived: 'bg-st-check text-st-check-fg',
  closed: 'bg-st-check text-st-check-fg',
  trashed: 'bg-destructive/10 text-destructive',
};

/** The action label for moving FROM the current status TO `to`. */
function transitionLabel(from: RoomLifecycleState, to: RoomLifecycleState): string {
  if (to === 'trashed') return 'Delete';
  if (to === 'archived') return 'Archive';
  return from === 'trashed' ? 'Restore' : 'Reactivate';
}

interface RoomConfigCardProps {
  room: Room;
  /** The role the admin has selected but not yet saved. */
  draftRole: RoomAdminRole;
  onDraftRoleChange: (role: RoomAdminRole) => void;
  /** Persist the draft role (PATCH). */
  onSave: () => void;
  pending: boolean;
  error: string | null;
  /** Advisory warnings from a 409 (role change with existing grants). */
  warnings: GrantWarning[];
  onProceed: () => void;
  onCancelWarnings: () => void;
  /** Change the room's status (active/archived/trashed). */
  onTransition: (to: RoomLifecycleState) => void;
  /** The lifecycle transition is in flight (disables the status buttons). */
  transitionPending: boolean;
  /** Error message from a failed / invalid lifecycle transition — for a plan-limit
   * 409 (`archived_limit_reached` etc.) this is the server's own `detail` text. */
  transitionError: string | null;
  /** Set when the lifecycle transition failed with a plan-limit 409 — shown as the
   * options dialog (`PlanLimitDialog`) instead of the raw error text. */
  transitionLimit: PlanLimitInfo | null;
  /** Dismiss the plan-limit dialog (resets the failed transition). */
  onDismissTransitionLimit: () => void;
  /** Restore a trashed room: starts the $500 recovery-fee checkout (redirects to Stripe). */
  onRecover: () => void;
  recoverPending: boolean;
  /** Error from the recovery call — the friendly "Billing isn't set up yet." on a dormant 503. */
  recoverError: string | null;
}

/**
 * PRD-0004 §3a Config panel — the room role (ADR-0010) and its derived buyer-
 * portal state, with the advise + acknowledge flow for changing the role once
 * grants exist. Presentational: the page owns the draft role, the mutation and
 * the pending/error/warning state.
 */
export function RoomConfigCard({
  room,
  draftRole,
  onDraftRoleChange,
  onSave,
  pending,
  error,
  warnings,
  onProceed,
  onCancelWarnings,
  onTransition,
  transitionPending,
  transitionError,
  transitionLimit,
  onDismissTransitionLimit,
  onRecover,
  recoverPending,
  recoverError,
}: RoomConfigCardProps) {
  const currentRole = room.adminRole ?? 'intermediary';
  const dirty = draftRole !== currentRole;
  const hasWarnings = warnings.length > 0;

  // Archive and Delete end or hide the room for the other side, so each gets a
  // confirmation step instead of firing onTransition immediately. Restore also
  // confirms (it costs the one-time recovery fee); Reactivate stays one click.
  const [confirming, setConfirming] = useState<'archive' | 'delete' | 'restore' | null>(null);

  // Once the transition actually lands (the status changes), dismiss the
  // confirmation — the status section re-renders into the new state on its own.
  // On a failure (e.g. a plan-limit 409) the modal stays open and shows the
  // server's message.
  useEffect(() => {
    setConfirming(null);
  }, [room.lifecycleState]);

  const transitions = ROOM_LIFECYCLE_TRANSITIONS[room.lifecycleState];
  const trashed = room.lifecycleState === 'trashed';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Configuration</CardTitle>
        <p className="text-sm text-muted-foreground">
          Your role for this room. Running the deal as the intermediary is the default; choose Buyer
          or Seller only if you&apos;re a principal acting without a broker. Your role sets the
          default access each party gets when you invite them.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Room status (lifecycle): Active / Archived / In Trash. Only the
            transitions the state machine allows are offered. */}
        <div className="space-y-2 border-b pb-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">Status:</span>
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLES[room.lifecycleState]}`}
            >
              {STATUS_LABELS[room.lifecycleState]}
            </span>
            <HelpTip id="admin.settings.status" />
          </div>
          {trashed ? (
            <p className="text-xs text-muted-foreground">
              In Trash — restorable for 30 days (a recovery fee applies)
            </p>
          ) : null}
          {transitions.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {transitions.map((to) => (
                <Button
                  key={to}
                  variant={to === 'trashed' ? 'outline' : 'default'}
                  size="sm"
                  disabled={transitionPending}
                  onClick={() =>
                    to === 'archived'
                      ? setConfirming('archive')
                      : to === 'trashed'
                        ? setConfirming('delete')
                        : trashed
                          ? setConfirming('restore')
                          : onTransition(to)
                  }
                >
                  {transitionLabel(room.lifecycleState, to)}
                </Button>
              ))}
            </div>
          ) : null}
          {/* The confirmation modals show the error themselves; show it here
              only when no modal is open (Reactivate / Restore, one-click). */}
          {transitionError && !transitionLimit && confirming === null ? (
            <p className="text-xs text-destructive">{transitionError}</p>
          ) : null}
        </div>

        {/* The buyer portal only exists for a Buyer-run room (a principal buyer
            running their own room). For the common intermediary/seller case it is
            always off and irrelevant, so the line is hidden entirely rather than
            showing a meaningless "Off". */}
        {room.buyerPortalActive ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">Buyer portal:</span>
            <span className="rounded-full bg-st-certified px-2.5 py-0.5 text-xs font-medium text-st-certified-fg">
              Active
            </span>
            <span className="text-xs text-muted-foreground">
              Buyers you invite can sign in to their own portal for this room.
            </span>
          </div>
        ) : null}

        <div className="space-y-1.5">
          <label htmlFor="admin-role" className="text-sm font-medium">
            Admin role
          </label>
          <select
            id="admin-role"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            value={draftRole}
            onChange={(e) => onDraftRoleChange(e.target.value as RoomAdminRole)}
            disabled={pending || hasWarnings}
          >
            {ROOM_ADMIN_ROLES.map((role) => (
              <option key={role} value={role}>
                {ROOM_ADMIN_ROLE_LABELS[role]}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">{ROOM_ADMIN_ROLE_HINTS[draftRole]}</p>
        </div>

        {hasWarnings ? (
          <GrantWarningList
            warnings={warnings}
            pending={pending}
            onProceed={onProceed}
            onCancel={onCancelWarnings}
          />
        ) : (
          <div className="flex items-center gap-3">
            <Button size="sm" onClick={onSave} disabled={!dirty || pending}>
              {pending ? 'Saving…' : 'Change role'}
            </Button>
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
          </div>
        )}
      </CardContent>

      {transitionLimit ? (
        <PlanLimitDialog
          info={transitionLimit}
          onClose={() => {
            setConfirming(null);
            onDismissTransitionLimit();
          }}
        />
      ) : null}

      {confirming && !transitionLimit ? (
        <Modal
          title={
            confirming === 'archive'
              ? 'Archive this room?'
              : confirming === 'restore'
                ? 'Restore this room?'
                : 'Delete this room?'
          }
          onClose={() => setConfirming(null)}
          footer={
            <>
              {(confirming === 'restore' ? recoverError : transitionError) ? (
                <p className="text-sm text-destructive">
                  {confirming === 'restore' ? recoverError : transitionError}
                </p>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirming(null)}
                  disabled={transitionPending || recoverPending}
                >
                  Cancel
                </Button>
                {confirming === 'restore' ? (
                  <Button size="sm" onClick={onRecover} disabled={recoverPending}>
                    {recoverPending ? 'Redirecting…' : 'Pay $500 & restore'}
                  </Button>
                ) : confirming === 'archive' ? (
                  <Button
                    size="sm"
                    onClick={() => onTransition('archived')}
                    disabled={transitionPending}
                  >
                    {transitionPending ? 'Archiving…' : 'Archive'}
                  </Button>
                ) : (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => onTransition('trashed')}
                    disabled={transitionPending}
                  >
                    {transitionPending ? 'Deleting…' : 'Delete'}
                  </Button>
                )}
              </div>
            </>
          }
        >
          <p className="text-sm">
            {confirming === 'restore'
              ? "Restoring a room from Trash carries a one-time $500 recovery fee. You'll be taken to secure checkout, and the room comes back once the payment completes."
              : confirming === 'archive'
                ? 'The deal ends for the other side: invited buyers and sellers lose access immediately. You (and any admins) keep a read-only view, and the room stays in your account. You can reactivate it anytime.'
              : "It moves to Trash for 30 days — you can restore it during that window (a recovery fee applies). After 30 days it can no longer be restored."}
          </p>
        </Modal>
      ) : null}
    </Card>
  );
}
